/**
 * Allowed retention values + labels — mirror of `api/common/data_retention.py`
 * `RETENTION_OPTIONS`. Keep the two in sync; the backend is the source of
 * truth and rejects anything else.
 *
 * `null` here means "retain forever" (legacy default for accounts that
 * pre-date this feature). `0` means "do not store" — the strictest
 * privacy posture, paired with ZDR for healthcare/BAA customers.
 */

export type RetentionDays = number | null;

export interface RetentionOption {
  value: RetentionDays;
  label: string;
  /** Long-form description shown next to the option in the UI. */
  description: string;
  /** True for the strictest privacy options — UI may emphasize them. */
  emphasize?: boolean;
}

export const RETENTION_OPTIONS: RetentionOption[] = [
  {
    value: null,
    label: 'Forever',
    description: 'Keep all run history indefinitely (default).',
  },
  {
    value: 0,
    label: 'Do not store',
    description:
      "Don't persist run history at all. Function and chain runs work, but " +
      'we never write the input/output to our database; only usage (model, ' +
      'tokens, timing) is recorded for billing. Pairs naturally with ' +
      'Zero Data Retention.',
    emphasize: true,
  },
  {
    value: 7,
    label: '7 days',
    description: 'Auto-delete run inputs and outputs older than 7 days (usage is kept for billing).',
  },
  {
    value: 14,
    label: '14 days',
    description: 'Auto-delete run inputs and outputs older than 14 days (usage is kept for billing).',
  },
  {
    value: 30,
    label: '30 days',
    description: 'Auto-delete run inputs and outputs older than 30 days (usage is kept for billing).',
  },
  {
    value: 60,
    label: '60 days',
    description: 'Auto-delete run inputs and outputs older than 60 days (usage is kept for billing).',
  },
  {
    value: 180,
    label: '180 days',
    description: 'Auto-delete run inputs and outputs older than 180 days (usage is kept for billing).',
  },
  {
    value: 365,
    label: '365 days',
    description: 'Auto-delete run inputs and outputs older than 365 days (usage is kept for billing).',
  },
];

export function describeRetention(value: RetentionDays): string {
  const opt = RETENTION_OPTIONS.find((o) => o.value === value);
  return opt?.label ?? (value === null ? 'Forever' : `${value} days`);
}

/**
 * Internal serialization for the <Select value="..."> binding. MUI's
 * Select can't bind to `null` directly, so we route through a sentinel
 * string and translate at the boundary.
 */
export const RETENTION_FOREVER_SENTINEL = '__forever__';

export function retentionToSentinel(value: RetentionDays): string {
  return value === null ? RETENTION_FOREVER_SENTINEL : String(value);
}

export function sentinelToRetention(sentinel: string): RetentionDays {
  if (sentinel === RETENTION_FOREVER_SENTINEL) return null;
  const n = Number(sentinel);
  return Number.isFinite(n) ? n : null;
}
